<template>
  <v-container
    fluid
    class="wr-page"
    :class="{ 'wr-page--dark': $vuetify.theme.dark }"
  >
    <section>
      <!-- Top Navigation Header (matches /report-bill style) -->
      <v-card class="wr-nav mb-3" flat>
        <v-card-text class="d-flex align-center flex-wrap py-2 px-3">
          <div class="d-flex align-center mr-4">
            <span class="br-accent-bar mr-2" />
            <h1 class="br-page-title mb-0">{{ $t("waste_requests") }}</h1>
            <span class="br-panel-count ml-2">{{ wasteNumber(total, 0) }} {{ $t("rows") }}</span>
          </div>

          <v-spacer />

          <v-btn
            v-if="canCreate"
            color="primary"
            depressed
            rounded
            small
            class="text-none font-weight-bold"
            to="/manage-waste"
          >
            <v-icon left small>fal fa-plus</v-icon>
            {{ $t("waste_new_record") }}
          </v-btn>
        </v-card-text>
      </v-card>

      <v-alert v-if="!canView" type="warning" text dense border="left" class="mb-3">
        {{ $t("waste_no_permission_view") }}
      </v-alert>

<KpiCards class="mb-3" :items="kpiItems" :loading="summaryLoading" :skeleton="4" />

      <!-- Main Panel (matches TabBills.vue .br-panel) -->
      <v-card class="br-panel mb-3" flat>
        <v-card-text class="pb-2 pt-3">
          <!-- Panel Header: Title + Status Tabs -->
          <div class="d-flex align-center flex-wrap br-panel-header">
            <span class="br-accent-bar" />
            <h2 class="br-panel-title">{{ $t("waste_requests") }}</h2>
            <span class="br-panel-count">{{ wasteNumber(total, 0) }} {{ $t("rows") }}</span>

            <v-spacer />

            <div class="br-tabs">
              <div
                class="br-tab"
                :class="{ 'br-tab--active': filters.status === 'all' }"
                @click="filters.status = 'all'"
              >
                {{ $t("all") }}
                <span class="br-tab-count">{{ totalSummaryCount }}</span>
              </div>
              <div
                v-for="st in statuses"
                :key="st"
                class="br-tab"
                :class="{ 'br-tab--active': filters.status === st }"
                @click="filters.status = st"
              >
                {{ $t(st) }}
                <span class="br-tab-count">{{ wasteNumber(summary[st].count, 0) }}</span>
              </div>
            </div>
          </div>

          <!-- Filter Row (matches TabBills.vue .br-filter-row) -->
          <div class="d-flex align-center flex-wrap br-filter-row mt-3">
            <TextSearch
              v-model="search"
              class="br-search"
              placeholder="waste_search_code"
              @search="applySearch"
              @onClear="clearSearch"
            />

            <div class="d-flex align-center br-date-range">
              <DatePicker
                v-model="filters.start_date"
                :max="filters.end_date"
                :disabled="!!search"
                :dense="true"
                :placeholder="false"
                label="start_date"
                hide-details
                style="max-width: 152px;"
              />
              <span class="br-date-arrow mx-1">→</span>
              <DatePicker
                v-model="filters.end_date"
                :min="filters.start_date"
                :disabled="!!search"
                :dense="true"
                :placeholder="false"
                label="end_date"
                hide-details
                style="max-width: 152px;"
              />
            </div>

            <TextSelect
              v-model="filters.doc_type"
              :items="docTypeItems"
              label="waste_doc_type"
              text="text"
              itemValue="value"
              :return-object="false"
              :searchable="false"
              :placeholder="false"
              :dense="true"
              class="br-select"
            />

            <TextSelect
              v-model="filters.waste_type"
              :items="wasteTypeFilterItems"
              label="waste_type"
              text="text"
              itemValue="value"
              :return-object="false"
              :searchable="false"
              :placeholder="false"
              :dense="true"
              class="br-select"
            />

            <span v-if="isFiltered" class="br-clear-link" @click="clearFilters">
              {{ $t("clear_filter") }}
            </span>

            <v-spacer />

            <div class="br-export-btn" @click="handleExcelExport">
              <v-icon size="13" class="mr-1">fal fa-file-export</v-icon>
              {{ $t("export") }}
            </div>
          </div>

          <div v-if="search" class="mt-2 text-caption grey--text d-flex align-center">
            <v-icon x-small class="mr-1">fal fa-info-circle</v-icon>
            {{ $t("waste_date_ignored_while_searching") }}
          </div>

          <!-- Table Container -->
          <div class="mt-3">
            <v-skeleton-loader
              v-if="loading && !items.length"
              type="table-thead, table-tbody, table-tfoot"
              aria-busy="true"
            />

            <v-data-table
              v-else
              class="br-data-table"
              :headers="headers"
              :items="items"
              :server-items-length="total"
              :page="page"
              :items-per-page="perPage"
              :loading="loading"
              :loading-text="$t('loading')"
              :no-data-text="$t('no_data')"
              :footer-props="{
                'items-per-page-options': [10, 25, 50],
                'items-per-page-text': $t('rows_per_page'),
              }"
              disable-sort
              @update:page="changePage"
              @update:items-per-page="changePerPage"
              @click:row="openDetail"
            >
              <!-- Code Column -->
              <template v-slot:item.code="{ item }">
                <div class="d-flex flex-column py-1">
                  <span class="font-weight-bold primary--text font-mono">{{ item.code }}</span>
                  <span v-if="item.doc_type !== 'waste'" class="text-caption grey--text">
                    {{ $t(`waste_doc_${item.doc_type}`) }}
                  </span>
                </div>
              </template>

              <!-- Status Pill -->
              <template v-slot:item.status="{ item }">
                <WasteStatusPill :status="item.status" />
              </template>

              <!-- User / Requested By -->
              <template v-slot:item.user="{ item }">
                <div class="d-flex align-center">
                  <span class="br-avatar mr-2">{{ initials(wasteUserName(item.user)) }}</span>
                  <span class="text-truncate font-weight-medium" style="max-width: 150px;">
                    {{ wasteUserName(item.user) }}
                  </span>
                </div>
              </template>

              <!-- Items Count -->
              <template v-slot:item.details_count="{ item }">
                <v-chip x-small label outlined class="font-weight-medium">
                  {{ item.details_count }} {{ $t("items") }}
                </v-chip>
              </template>

              <!-- Photos Count -->
              <template v-slot:item.images_count="{ item }">
                <span class="d-inline-flex align-center text-caption grey--text text--darken-1">
                  <v-icon x-small class="mr-1">fal fa-image</v-icon>{{ item.images_count }}
                </span>
              </template>

              <!-- Total Cost -->
              <template v-slot:item.total_cost="{ item }">
                <span
                  class="font-weight-bold font-mono"
                  :class="Number(item.total_cost) < 0 ? 'success--text' : 'error--text'"
                >
                  {{ wasteNumber(item.total_cost) }} ₭
                </span>
              </template>

              <!-- Date -->
              <template v-slot:item.date="{ item }">
                <span class="text-caption grey--text text--darken-2 font-mono">
                  {{ wasteDate(item.date, false) }}
                </span>
              </template>

              <!-- Actions -->
              <template v-slot:item.actions="{ item }">
                <div class="d-flex justify-center" @click.stop>
                  <button
                    type="button"
                    class="ps-act"
                    :title="$t('view')"
                    @click="openDetail(item)"
                  >
                    <v-icon x-small>fal fa-eye</v-icon>
                  </button>
                </div>
              </template>

              <!-- Empty State -->
              <template v-slot:no-data>
                <div class="ps-empty text-center py-10">
                  <v-icon size="48" class="ps-empty-icon mb-2">fal fa-inbox</v-icon>
                  <div class="ps-empty-text font-weight-bold">{{ $t("no_data") }}</div>
                </div>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>
    </section>

    <!-- Detail Dialog -->
    <WasteDetailDialog
      v-model="dialog"
      :waste-id="selectedId"
      :can-approve="canApprove"
      :user-id="user && user.id"
      @changed="refresh"
    />
  </v-container>
</template>

<script>
import moment from "moment";
import { mapMutations } from "vuex";
import { myMixin } from "@/mixin/main";
import { wasteMixin, WASTE_STATUSES } from "@/mixin/waste";
import WasteDetailDialog from "@/components/waste/WasteDetailDialog";
import WasteStatusPill from "@/components/waste/WasteStatusPill";
import TextSelect from "@/components/global/TextSelect";
import { toast } from "@/controllers/alert";

const DEFAULT_RANGE_DAYS = 29;

const defaultFilters = () => ({
  status: "all",
  doc_type: "all",
  waste_type: "all",
  start_date: moment().subtract(DEFAULT_RANGE_DAYS, "days").format("YYYY-MM-DD"),
  end_date: moment().format("YYYY-MM-DD"),
});

const emptySummary = () => ({
  pending: { count: 0, total_cost: 0 },
  approved: { count: 0, total_cost: 0 },
  rejected: { count: 0, total_cost: 0 },
  cancelled: { count: 0, total_cost: 0 },
});

export default {
  name: "WasteRequests",

  mixins: [myMixin, wasteMixin],

  components: { WasteDetailDialog, WasteStatusPill, TextSelect },

  data() {
    return {
      filters: defaultFilters(),
      search: "",
      statuses: WASTE_STATUSES,
      items: [],
      total: 0,
      page: 1,
      perPage: 25,
      loading: true,
      summary: emptySummary(),
      summaryLoading: true,
      dialog: false,
      selectedId: null,
      cards: [
        { status: "pending", icon: "fal fa-hourglass-half", color: "warning" },
        { status: "approved", icon: "fal fa-check-circle", color: "success" },
        { status: "rejected", icon: "fal fa-times-circle", color: "error" },
        { status: "cancelled", icon: "fal fa-ban", color: "grey darken-1" },
      ],
    };
  },

  computed: {
    kpiItems() {
      return this.cards.map((card) => {
        const row = this.summary[card.status] || { count: 0, total_cost: 0 };
        return {
          key: card.status,
          title: card.status,
          icon: card.icon,
          iconColor: card.color,
          tone: card.status,
          value: this.wasteNumber(row.count, 0),
          note: `${this.$t("waste_cost_impact")}: ${this.wasteNumber(row.total_cost)} ₭`,
        };
      });
    },

    canApprove() {
      return this.MyRole("approve_waste");
    },

    canCreate() {
      return this.MyRole("create_waste");
    },

    canView() {
      return this.canApprove || this.canCreate;
    },

    totalSummaryCount() {
      return Object.values(this.summary).reduce(
        (acc, curr) => acc + (Number(curr.count) || 0),
        0
      );
    },

    isFiltered() {
      return (
        !!this.search ||
        this.filters.status !== "all" ||
        this.filters.doc_type !== "all" ||
        this.filters.waste_type !== "all" ||
        this.filters.start_date !== moment().subtract(DEFAULT_RANGE_DAYS, "days").format("YYYY-MM-DD") ||
        this.filters.end_date !== moment().format("YYYY-MM-DD")
      );
    },

    headers() {
      return [
        { text: this.$t("code"), value: "code", sortable: false, width: "130px" },
        { text: this.$t("status"), value: "status", sortable: false, width: "120px" },
        { text: this.$t("requested_by"), value: "user", sortable: false },
        { text: this.$t("items"), value: "details_count", align: "center", sortable: false, width: "90px" },
        { text: this.$t("waste_col_photos"), value: "images_count", align: "center", sortable: false, width: "80px" },
        { text: this.$t("waste_cost_impact"), value: "total_cost", align: "right", sortable: false, width: "140px" },
        { text: this.$t("date"), value: "date", align: "right", sortable: false, width: "130px" },
        { text: this.$t("manage"), value: "actions", align: "center", sortable: false, width: "80px" },
      ];
    },

    docTypeItems() {
      return [
        { value: "all", text: this.$t("all") },
        { value: "waste", text: this.$t("waste_doc_waste") },
        { value: "wrong_usage", text: this.$t("waste_doc_wrong_usage") },
        { value: "reversal", text: this.$t("waste_doc_reversal") },
      ];
    },

    wasteTypeFilterItems() {
      return [{ value: "all", text: this.$t("all") }, ...this.wasteTypeItems];
    },
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.page = 1;
        this.refresh();
      },
    },
  },

  created() {
    this.requestSeq = 0;
    this.summarySeq = 0;
    this.refresh();
  },

  methods: {
    ...mapMutations({
      SET_OVERLAY: "index/SET_OVERLAY",
    }),

    initials(name) {
      if (!name) return "?";
      const parts = String(name).trim().split(/\s+/);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },

    query() {
      const params = new URLSearchParams();
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) params.set(key, value);
      });
      if (this.search) params.set("q", this.search);
      return params;
    },

    refresh() {
      if (!this.canView) {
        this.loading = false;
        this.summaryLoading = false;
        return;
      }
      this.fetchList();
      this.fetchSummary();
    },

    async fetchList() {
      const seq = ++this.requestSeq;
      this.loading = true;
      const params = this.query();
      params.set("page", this.page);
      params.set("per_page", this.perPage);
      try {
        const { data } = await this.$http.get(`stock_waste?${params.toString()}`);
        if (seq !== this.requestSeq) return;
        if (data.status) {
          this.items = data.data.data || [];
          this.total = data.data.total || 0;
        }
      } catch (err) {
        // the axios interceptor already showed the API's message
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },

    async fetchSummary() {
      const seq = ++this.summarySeq;
      this.summaryLoading = true;
      const params = this.query();
      params.delete("status");
      try {
        const { data } = await this.$http.get(`stock_waste_summary?${params.toString()}`);
        if (seq !== this.summarySeq) return;
        if (data.status) this.summary = { ...emptySummary(), ...data.data };
      } catch (err) {
        if (seq === this.summarySeq) this.summary = emptySummary();
      } finally {
        if (seq === this.summarySeq) this.summaryLoading = false;
      }
    },

    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchList();
    },

    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.perPage = perPage;
      this.page = 1;
      this.fetchList();
    },

    toggleStatus(status) {
      this.filters.status = this.filters.status === status ? "all" : status;
    },

    applySearch() {
      this.page = 1;
      this.refresh();
    },

    clearSearch() {
      this.search = "";
      this.applySearch();
    },

    clearFilters() {
      const hadSearch = !!this.search;
      this.search = "";
      this.filters = defaultFilters();
      if (hadSearch) this.applySearch();
    },

    openDetail(item) {
      this.selectedId = item.id;
      this.dialog = true;
    },

    async handleExcelExport() {
      if (!this.canView) return;
      try {
        this.SET_OVERLAY(true);
        const params = this.query();
        params.set("locale", this.$i18n.locale);
        const data = await this.$http.get(`stock_waste_export?${params.toString()}`, {
          responseType: "blob",
        });

        const blobUrl = window.URL.createObjectURL(new Blob([data.data]));
        const link = document.createElement("a");
        const fileName = `waste_requests_${this.filters.start_date}_to_${this.filters.end_date}.xlsx`;

        link.href = blobUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        toast(this, "excel_export_success");
      } catch (err) {
        console.error("Error exporting Excel:", err);
        toast(this, "excel_export_failed", "error");
      } finally {
        this.SET_OVERLAY(false);
      }
    },
  },
};
</script>

<style scoped>
.wr-page {
  --br-accent: var(--v-primary-base, #5b39c9);
  --br-accent-soft: rgba(91, 57, 201, 0.12);
  --br-accent-softer: rgba(91, 57, 201, 0.07);
  --br-accent-border: rgba(91, 57, 201, 0.35);
  --br-border: #e2e8f0;
  --br-muted: #94a3b8;
  --br-text: #141021;
  --br-radius-lg: 16px;
  --br-radius-md: 10px;
  max-width: 100%;
  background: #f6f6fa;
}

.wr-page--dark {
  --br-text: #ffffff;
  --br-border: rgba(255, 255, 255, 0.08);
  background: #1b1d30;
}

.wr-page--dark .wr-nav,
.wr-page--dark .br-panel,
.wr-page--dark .br-panel.v-card.v-sheet {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.wr-page section {
  width: 100%;
}

.wr-nav {
  background: #ffffff !important;
  border: 1px solid var(--br-border) !important;
  border-radius: 14px !important;
  box-shadow: none !important;
}

.br-page-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--br-text);
}

/* Panel */
.br-panel,
.br-panel.v-card.v-sheet {
  background: #ffffff !important;
  border: 1px solid var(--br-border) !important;
  border-radius: 16px !important;
  box-shadow: none !important;
}

.br-panel-header {
  gap: 10px;
}

.br-accent-bar {
  width: 3px;
  height: 16px;
  background: var(--br-accent);
  border-radius: 2px;
}

.br-panel-title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: var(--br-text);
}

.br-panel-count {
  font-size: 12px;
  color: var(--br-muted);
}

.br-tabs {
  display: flex;
  height: 38px;
  box-sizing: border-box;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 10px;
  background: #f1f2f7;
  border: 1px solid var(--br-border);
}

.br-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 4px 12px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  color: var(--br-muted);
  transition: all 0.15s ease;
}

.br-tab--active {
  color: #5b39c9 !important;
  background: #ffffff !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  font-weight: 700;
}

.br-page--dark .br-tab--active {
  color: #c4b5fd !important;
  background: rgba(154, 140, 240, 0.18) !important;
  box-shadow: none;
}

.br-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 17px;
  height: 17px;
  margin-left: 4px;
  padding: 0 4px;
  border-radius: 999px;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  background: rgba(148, 163, 184, 0.14);
}

.br-tab--active .br-tab-count {
  background: rgba(91, 57, 201, 0.14);
  color: #5b39c9;
}

.br-page--dark .br-tab--active .br-tab-count {
  background: rgba(167, 139, 250, 0.25);
  color: #c4b5fd;
}

/* Filter row */
.br-filter-row {
  gap: 10px;
}

.br-search {
  flex: 1 1 200px;
  min-width: 180px;
  max-width: 280px;
}

.br-date-range {
  display: flex;
  align-items: center;
}

.br-date-arrow {
  color: var(--br-muted);
}

.br-select {
  max-width: 170px;
}

.br-clear-link {
  font-size: 12px;
  color: #ef4444;
  cursor: pointer;
  white-space: nowrap;
}

.br-clear-link:hover {
  text-decoration: underline;
}

.br-export-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid var(--br-border);
  background: #ffffff;
  color: var(--br-text);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease;
}

.br-export-btn:hover {
  border-color: var(--br-accent);
  color: var(--br-accent);
  background: var(--br-accent-soft);
}

/* Table */
.br-data-table >>> .v-data-table__wrapper table {
  border-collapse: collapse;
}

.br-data-table >>> .v-data-table__wrapper table thead th {
  color: #64748b;
  font-weight: 700;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
  border-bottom: 1px solid #e2e8f0;
  height: 40px;
  white-space: nowrap;
}

.br-data-table >>> .v-data-table__wrapper table tbody tr {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.br-data-table >>> .v-data-table__wrapper table tbody tr:hover {
  background-color: #f8fafc !important;
}

.br-data-table >>> .v-data-table__wrapper table tbody td {
  border-bottom: 1px solid #f1f5f9;
  padding: 10px 14px;
  font-size: 13px;
}

.br-avatar {
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: var(--br-accent);
  background: var(--br-accent-soft);
}

.ps-act {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ps-act:hover {
  color: #5b39c9;
  border-color: #5b39c9;
  background: rgba(91, 57, 201, 0.06);
}

.ps-empty {
  color: #94a3b8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.ps-empty-icon {
  color: #cbd5e1 !important;
}

.ps-empty-text {
  font-size: 14px;
  color: #475569;
}

/* Dark mode overrides */
.br-page--dark .br-export-btn,
.theme--dark .br-export-btn {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.12);
  color: #e2e8f0;
}

.br-page--dark .br-export-btn:hover,
.theme--dark .br-export-btn:hover {
  border-color: #818cf8;
  color: #c4b5fd;
  background: rgba(154, 140, 240, 0.18);
}

.br-page--dark .br-data-table >>> .v-data-table__wrapper table thead th,
.theme--dark .br-data-table >>> .v-data-table__wrapper table thead th {
  background-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
  color: #94a3b8;
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.br-page--dark .br-data-table >>> .v-data-table__wrapper table tbody td,
.theme--dark .br-data-table >>> .v-data-table__wrapper table tbody td {
  border-bottom-color: rgba(255, 255, 255, 0.06);
  color: #f1f5f9;
}

.br-page--dark .br-data-table >>> .v-data-table__wrapper table tbody tr:hover,
.theme--dark .br-data-table >>> .v-data-table__wrapper table tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.03) !important;
}

.br-page--dark .ps-act,
.theme--dark .ps-act {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.12);
  color: #cbd5e1;
}

.br-page--dark .ps-act:hover,
.theme--dark .ps-act:hover {
  color: #c4b5fd;
  border-color: #818cf8;
  background: rgba(154, 140, 240, 0.18);
}

.br-page--dark .ps-empty-text,
.theme--dark .ps-empty-text {
  color: #94a3b8;
}
</style>
